import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, X, FileText, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_EXT = ["pdf", "doc", "docx", "rtf", "txt", "odt"];

export default function FooterContactForm({ variant = "dark" }) {
  const light = variant === "light";
  const inputCls = light
    ? "h-11 bg-background border-border text-foreground placeholder:text-foreground/45 focus-visible:ring-brand"
    : "h-11 bg-background/5 border-background/15 text-background placeholder:text-background/45 focus-visible:ring-brand";
  const textareaCls = light
    ? "bg-background border-border text-foreground placeholder:text-foreground/45 focus-visible:ring-brand resize-none"
    : "bg-background/5 border-background/15 text-background placeholder:text-background/45 focus-visible:ring-brand resize-none";
  const introCls = light ? "text-sm text-foreground/70 mb-2" : "text-sm text-background/70 mb-2";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    attachment_id: "",
    attachment_filename: "",
  });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Unsupported type. Use ${ALLOWED_EXT.join(", ")}.`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload-cv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        attachment_id: res.data.upload_id,
        attachment_filename: res.data.filename,
      }));
      toast.success("File attached");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () =>
    setForm((f) => ({ ...f, attachment_id: "", attachment_filename: "" }));

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        message: form.message || "",
        attachment_id: form.attachment_id || undefined,
        attachment_filename: form.attachment_filename || undefined,
      });
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        message: "",
        attachment_id: "",
        attachment_filename: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Could not send. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="footer-contact-form"
      onSubmit={submit}
      className="space-y-3"
    >
      <p className={introCls}>
        Quick question? Send us a message — we'll reply by email.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <Label htmlFor="fc-name" className="sr-only">Name</Label>
          <Input
            id="fc-name"
            data-testid="footer-contact-name"
            value={form.name}
            onChange={change("name")}
            placeholder="Name *"
            className={inputCls}
          />
          {errors.name && (
            <p className="text-[11px] text-red-400 mt-1">{errors.name}</p>
          )}
        </div>
        <div className="col-span-2 sm:col-span-1">
          <Label htmlFor="fc-email" className="sr-only">Email</Label>
          <Input
            id="fc-email"
            type="email"
            data-testid="footer-contact-email"
            value={form.email}
            onChange={change("email")}
            placeholder="Email *"
            className={inputCls}
          />
          {errors.email && (
            <p className="text-[11px] text-red-400 mt-1">{errors.email}</p>
          )}
        </div>
        <div className="col-span-2">
          <Label htmlFor="fc-phone" className="sr-only">Phone</Label>
          <Input
            id="fc-phone"
            data-testid="footer-contact-phone"
            value={form.phone}
            onChange={change("phone")}
            placeholder="Phone (optional)"
            className={inputCls}
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="fc-message" className="sr-only">Message</Label>
          <Textarea
            id="fc-message"
            data-testid="footer-contact-message"
            value={form.message}
            onChange={change("message")}
            placeholder="Comments (optional)"
            rows={3}
            className={textareaCls}
          />
        </div>
        <div className="col-span-2">
          {form.attachment_id ? (
            <div
              data-testid="footer-contact-attachment"
              className="flex items-center justify-between gap-2 rounded-lg border border-brand/40 bg-brand/10 px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 text-brand-300 shrink-0" />
                <span className="text-xs truncate">
                  {form.attachment_filename}
                </span>
              </div>
              <button
                type="button"
                onClick={removeFile}
                data-testid="footer-contact-remove-file"
                className="shrink-0 text-background/70 hover:text-background"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <label
              htmlFor="fc-file"
              data-testid="footer-contact-attach"
              className={`inline-flex items-center gap-2 text-xs text-background/70 hover:text-background cursor-pointer underline-offset-2 hover:underline ${
                uploading ? "opacity-60 pointer-events-none" : ""
              }`}
            >
              <Paperclip className="h-3.5 w-3.5" />
              {uploading ? "Uploading..." : "Attach a file (optional)"}
              <input
                id="fc-file"
                type="file"
                accept=".pdf,.doc,.docx,.rtf,.txt,.odt"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                disabled={uploading}
              />
            </label>
          )}
        </div>
      </div>
      <button
        type="submit"
        data-testid="footer-contact-submit"
        disabled={submitting || uploading}
        className="btn-primary inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60"
      >
        {submitting ? "Sending..." : "Send message"}
        <Send className="h-4 w-4" />
      </button>
    </form>
  );
}
